<script>
import BaseDropdownMenu from '@/components/_base-dropdown-menu.vue'
import BookingCardPolicies from '@/components/booking/booking-card-policies.vue'
import CandidateImage from '@/components/candidate/candidate-image.vue'
import baseMixin from '@/mixins/baseMixin'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import formatDateMixin from '@/mixins/formatDateMixin'
import EmitEvent from '@/models/general/emitEvent'
import Interaction from '@/models/general/interaction'
import MenuItem from '@/models/general/menuItem'
import dayjs from '@/services/date'
import BookingCard from '@/shared/constants/booking/BookingCard'
import { BookingCommentType } from '@/shared/constants/booking/BookingCommentType'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import BookingType from '@/shared/constants/booking/BookingType'
import ClientBookingStatus from '@/shared/constants/booking/ClientBookingStatus'
import InteractionType from '@/shared/constants/core/InteractionType'
import VuetifyColorClass from '@/shared/constants/core/VuetifyColourClass'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import BookingListFeedback from '@components/booking/booking-list-feedback.vue'
import GradeList from '@components/booking/grade-list.vue'
import CertPills from '@components/cert-pills.vue'
import { mapGetters } from 'vuex'
import BookingCardIdLabel from './booking-card-id-label.vue'
import BookingStatusLabel from './booking-status-label.vue'
import ExtendBookingModal from './extend-booking/extend-booking-modal.vue'

export default {
  name: 'BookingCard',
  components: {
    CertPills,
    BookingCardPolicies,
    GradeList,
    BookingListFeedback,
    CandidateImage,
    BookingCardIdLabel,
    BookingStatusLabel,
    BaseDropdownMenu,
    ExtendBookingModal,
  },
  mixins: [baseMixin, formatDateMixin, featureFlagMixin],
  props: {
    booking: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      profileImgSrc: null,
      showExtendBookingModal: false,
      BookingStatusLabelType,
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
      hasLocationBookingCreatePermission:
        'auth/hasLocationBookingCreatePermission',
    }),
    howLate() {
      if (!this.booking.runningLate) return 0

      const items = this.booking.noteList.filter(
        (item) => item.type === BookingCommentType.LATE
      )
      if (items.length === 0) return 0

      const jsonObj = JSON.parse(items[0].text)
      return jsonObj.LateMins
    },
    hasGrades() {
      return this.booking.grades.length > 0
    },
    displayCandidate() {
      if (this.isMultiTentative) {
        return this.booking?.tentativeCandidates?.length === 1
      }

      return this.booking.candidate && this.booking.candidate.id > 0
    },
    compRunningLateClass() {
      return !this.booking.runningLate ? '' : 'red--text font-weight-medium'
    },
    compDuration() {
      return `${this.renderTime24(
        this.booking.startDateTimeLocal
      )} - ${this.renderTime24(this.booking.endDateTimeLocal)}`
    },
    compFilledCandidate() {
      if (!this.displayCandidate)
        return { id: -1, firstName: null, lastName: null }

      return this.isMultiTentative
        ? this.booking.tentativeCandidates[0]
        : this.booking.candidate
    },
    compCandidateFullName() {
      if (!this.compFilledCandidate) return ''

      return `${this.compFilledCandidate.firstName} ${this.compFilledCandidate.lastName}`
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    isMultiTentative() {
      return this.booking.status === ClientBookingStatus.multiTentative
    },
    isUnapproved() {
      return this.bookingStatus === BookingCard.labelStatus.Unapproved
    },
    bookingStatus() {
      return BookingCard.bookingStatusMap[this.booking.status]
    },
    isUnfilled() {
      return this.bookingStatus === BookingCard.labelStatus.Open
    },
    buttonConfig() {
      const { warning200, blue200, warning, blue, grey500 } = VuetifyColorClass
      return {
        hoverColor: this.isUnfilled ? warning200 : blue200,
        iconColor: grey500,
        iconHoverColor: this.isUnfilled ? warning : blue,
      }
    },
    hasCreateForLocationPerm() {
      const locationId = this.booking?.location?.id

      return locationId && this.hasLocationBookingCreatePermission(locationId)
    },
    cardOptions() {
      const isToday =
        dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        ) ===
        dayjs()
          .tz(this.booking.location.timeZone)
          .format(DateFormatToken.YYYYMMDD)

      const options = [
        {
          icon: 'mdi-arrow-top-right-bottom-left',
          type: 'action',
          text: this.$t('booking.extend.extend'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'extendBooking',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableExtendBooking,
            this.hasCreateForLocationPerm,
          ],
        },
        {
          id: isToday
            ? 'edit-today-booking-menu-item'
            : 'edit-day-booking-menu-item',
          icon: 'mdi-calendar-edit-outline',
          type: 'action',
          text: this.$t('booking.editBookingDay'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingDay',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableEditBooking,
            this.canCreateBookings,
            this.bookingStatus === BookingStatusLabelType.Open,
            dayjs(this.booking.startDateTimeLocal).format(
              DateFormatToken.YYYYMMDD
            ) >=
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD),
          ],
          data: {
            disabled: isToday,
          },
        },
        {
          icon: 'mdi-pencil-outline',
          type: 'action',
          text: this.$t('booking.editBookingBlock'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'editBookingBlock',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableEditBooking,
            this.canCreateBookings,
            this.bookingStatus === BookingStatusLabelType.Open,
            this.booking.blockDetails &&
              this.booking.blockDetails.bookingsCount ===
                this.booking.blockDetails.openCount &&
              dayjs(this.booking.blockDetails.startDateLocal).format(
                DateFormatToken.YYYYMMDD
              ) >
                dayjs()
                  .tz(this.booking.location.timeZone)
                  .format(DateFormatToken.YYYYMMDD),
          ],
        },
        {
          id: 'duplicate-booking-menu-item',
          icon: 'mdi-content-copy',
          type: 'action',
          text: this.$t('booking.duplicate'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'duplicateBooking',
              value: this.booking.id,
            }),
          }),
          rules: [
            this.selfServiceFeatureFlags.enableDuplicateBooking,
            this.canCreateBookings,
          ],
          data: {
            disabled: ![
              BookingStatusLabelType.Confirmed,
              BookingStatusLabelType.Pending,
              BookingStatusLabelType.Open,
            ].includes(this.bookingStatus),
          },
        },
        {
          icon: 'mdi-close-circle-outline',
          type: 'action',
          text: this.$t('booking.cancelBookingBtn'),
          interaction: new Interaction({
            type: InteractionType.emitEvent,
            value: new EmitEvent({
              event: 'cancelBooking',
              value: this.booking.id,
            }),
          }),
          rules: [this.canCreateBookings],
        },
      ]

      const filteredOptions = options.filter((option) =>
        option.rules.every((rule) => rule)
      )

      return filteredOptions.map((option) => new MenuItem(option))
    },
  },
  methods: {
    viewCandidate() {
      if (this.displayCandidate) {
        this.$emit('viewCandidate', this.compFilledCandidate.id)
      }
    },
    updateProfileImage(data) {
      this.profileImgSrc = data
    },
    showCancelBookingDialog() {
      this.$emit('cancelBooking', this.booking)
    },
    toggleExtendBookingModal() {
      this.showExtendBookingModal = !this.showExtendBookingModal
    },
    disableMenuItem(menuItem) {
      if (!menuItem) return false

      switch (menuItem.id) {
        case 'edit-today-booking-menu-item':
        case 'duplicate-booking-menu-item':
          return menuItem.data.disabled
        default:
          return false
      }
    },
    editBookingDay() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.SingleDay,
          date: dayjs(this.booking.startDateTimeLocal).format(
            DateFormatToken.YYYYMMDD
          ),
        },
      })
    },
    editBookingBlock() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.Block,
        },
      })
    },
    duplicateBooking() {
      this.$router.push({
        name: routeDefinitions.bookingsDuplicate.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.SingleDay,
          date: dayjs(this.booking.startDateTimeLocal).format(
            DateFormatToken.YYYYMMDD
          ),
        },
      })
    },
  },
}
</script>

<template>
  <v-card class="mb-5 booking-card custom-elevation rounded-xl" elevation="0">
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6" lg="5" xl="4" class="d-flex">
          <div>
            <v-lazy>
              <CandidateImage
                :id="compFilledCandidate.id"
                :first-name="compFilledCandidate.firstName"
                :last-name="compFilledCandidate.lastName"
                rounded
                size="xxxl"
                clickable
                @profileImgSrc="updateProfileImage"
                @click="viewCandidate"
              />
            </v-lazy>
          </div>

          <div class="flex-grow-1">
            <BookingCardIdLabel :booking="booking" />

            <h5 class="font-weight-medium subtitle-1">
              <a
                v-if="displayCandidate"
                title="View Profile"
                @click="viewCandidate"
              >
                {{ compCandidateFullName }}
              </a>
              <span v-else-if="isMultiTentative">
                {{ this.$t('booking.multipleEducators') }}
              </span>
              <span v-else>
                {{ this.$t('booking.unfilledBookingText') }}
              </span>
            </h5>

            <p
              :class="[
                'subtitle-1 font-weight-medium mb-0',
                compRunningLateClass,
              ]"
            >
              {{ compDuration }}
              <span
                v-if="booking.runningLate"
                class="booking-card__running-late"
              >
                +{{ $t('booking.minsLateText', { count: howLate }) }}
              </span>
              <span
                title="Classification"
                class="booking-card__classification caption my-0"
              >
                {{ booking.type }}
              </span>
            </p>

            <div v-if="booking.replacing" class="booking-card__replacing">
              {{ $t('booking.replacingLabel') }}:
              <span class="d-inline-block font-weight-medium">
                {{
                  `${booking.replacing.firstName} ${booking.replacing.lastName}`
                }}
              </span>
            </div>

            <GradeList v-if="hasGrades" small :grades="booking.grades" />

            <div v-if="displayCandidate" class="badges mb-2">
              <CertPills :certifications="booking.certChecks" />
              <BookingCardPolicies :policies="booking.agreements" />
            </div>

            <div>
              <BookingStatusLabel :status="bookingStatus" />
            </div>
          </div>
        </v-col>
        <v-col cols="12" md="6" lg="5" xl="6">
          <div class="booking-card__details">
            <div v-if="booking.roomNumber" class="booking-card__room">
              {{ $t('booking.roomLabel') }}:
              <span class="font-weight-medium"> {{ booking.roomNumber }} </span>
            </div>

            <div v-if="booking.notes" class="booking-card__notes">
              {{ $t('booking.notesLabel') }}:
              <span class="ws-pre-wrap">
                {{ booking.notes }}
              </span>
            </div>
          </div>
        </v-col>

        <v-col class="d-flex align-center justify-end" cols="12" md="12" lg="2">
          <div
            v-if="canCreateBookings"
            class="text-right d-flex flex-row-reverse align-center"
            data-button="booking-option"
          >
            <BaseDropdownMenu
              v-bind="buttonConfig"
              :menu-items="cardOptions"
              tertiary
              :disabled="isUnapproved"
              :disable-list-item="disableMenuItem"
              @duplicateBooking="duplicateBooking"
              @editBookingDay="editBookingDay"
              @editBookingBlock="editBookingBlock"
              @cancelBooking="showCancelBookingDialog"
              @extendBooking="toggleExtendBookingModal"
            />

            <BookingListFeedback
              v-if="displayCandidate && !isUnapproved"
              :booking="booking"
            />
          </div>
        </v-col>
      </v-row>

      <div class="clearfix"> </div>
    </v-card-text>
    <ExtendBookingModal
      v-model="showExtendBookingModal"
      :booking="booking"
      :is-unfilled="isUnfilled"
    />
  </v-card>
</template>
